import { firstValueFrom, take } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import {
  CancelRecurrentPaymentRequest,
  CancelRecurrentPaymentResponse,
  CheckEmailRequest,
  CheckEmailResponse,
  CheckIdentityRequest,
  CheckIdentityResponse,
  CheckoutCheckRequest,
  CheckoutCheckResponse,
  CheckoutRequest,
  CheckoutResponse,
  CouponRequest,
  CouponResponse,
  FreeTrialCheckoutRequest,
  FreeTrialCheckoutResponse,
  FreeTrialProductsResponse,
  InstallmentsRequest,
  InstallmentsResponse,
  PaymentDataRequest,
  PaymentDataResponse,
  ProductsResponse,
  RegisterKeycloakRequest,
  RegisterKeycloakResponse,
  RegisterUserDataRequest,
  RegisterUserDataResponse,
  SupportPermissionsRequest,
  SupportPermissionsResponse,
  UserDataResponse
} from '../../models';

export const publicUrls = ['/v1/products', '/v1/check_email', '/v1/register_keycloak', '/v1/register'];
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly http = inject(HttpClient);

  private apiUrl = environment.apiUrl;

  //Register data
  registerUserKC(payload: RegisterKeycloakRequest): Promise<RegisterKeycloakResponse> {
    return firstValueFrom(this.http.post<RegisterKeycloakResponse>(
      `${this.apiUrl}/v1/register_keycloak`,
      payload).pipe(take(1)));
  }

  registerUserData(payload: RegisterUserDataRequest): Promise<RegisterUserDataResponse> {
    return firstValueFrom(this.http.post<RegisterUserDataResponse>(
      `${this.apiUrl}/v1/register`,
      payload).pipe(take(1)));
  }

  /**
   * `POST /v2/checkout` requires an `Idempotency-Key` header. Pass the same key
   * when retrying the same order so the gateway does not charge twice; use a
   * fresh one for a genuinely new attempt.
   */
  checkoutOrder(payload: CheckoutRequest, idempotencyKey: string): Promise<CheckoutResponse> {
    return firstValueFrom(this.http.post<CheckoutResponse>(
      `${this.apiUrl}/v2/finalizar_pedido`,
      payload,
      { headers: { 'Idempotency-Key': idempotencyKey } }).pipe(take(1)));
  }

  checkoutTrialOrder(payload: FreeTrialCheckoutRequest): Promise<FreeTrialCheckoutResponse> {
    return firstValueFrom(this.http.post<FreeTrialCheckoutResponse>(
      `${this.apiUrl}/v2/finalizar_pedido/teste_gratuito`,
      payload).pipe(take(1)));
  }

  //Cancel data
  cancelOrder(payload: CancelRecurrentPaymentRequest): Promise<CancelRecurrentPaymentResponse> {
    return firstValueFrom(this.http.post<CancelRecurrentPaymentResponse>(
      `${this.apiUrl}/v2/pagamento_recorrente/cancelar`,
      payload).pipe(take(1)));
  }

  //Validate data
  checkCouponData(payload: CouponRequest): Promise<CouponResponse> {
    return firstValueFrom(this.http.post<CouponResponse>(
      `${this.apiUrl}/v2/cupom_desconto`,
      payload).pipe(take(1)));
  }

  checkEmail(payload: CheckEmailRequest): Promise<CheckEmailResponse> {
    return firstValueFrom(this.http.post<CheckEmailResponse>(
      `${this.apiUrl}/v1/check_email`,
      payload).pipe(take(1)));
  }

  checkoutCheck(payload: CheckoutCheckRequest): Promise<CheckoutCheckResponse> {
    return firstValueFrom(this.http.post<CheckoutCheckResponse>(
      `${this.apiUrl}/v2/finalizar_pedido/verificar`,
      payload).pipe(take(1)));
  }

  checkCPFAndCNPJ(payload: CheckIdentityRequest): Promise<CheckIdentityResponse> {
    return firstValueFrom(this.http.post<CheckIdentityResponse>(
      `${this.apiUrl}/v1/register/check_cpf_cnpj`,
      payload).pipe(take(1)));
  }

  checkShoppingCartValidity(payload: SupportPermissionsRequest): Promise<SupportPermissionsResponse> {
    return firstValueFrom(this.http.post<SupportPermissionsResponse>(
      `${this.apiUrl}/v1/suporte_table/permissions`,
      payload).pipe(take(1)));
  }

  //Get data
  getUserData(): Promise<UserDataResponse> {
    return firstValueFrom(this.http.get<UserDataResponse>(
      `${this.apiUrl}/v2/dados_usuario`).pipe(take(1)));
  }

  getProductData(): Promise<ProductsResponse> {
    return firstValueFrom(this.http.get<ProductsResponse>(
      `${this.apiUrl}/v1/products`).pipe(take(1)));
  }

  getFreeTrialProductData(): Promise<FreeTrialProductsResponse> {
    return firstValueFrom(this.http.get<FreeTrialProductsResponse>(
      `${this.apiUrl}/v2/produtos/teste_gratuito`).pipe(take(1)));
  }

  getOrdersData(payload: PaymentDataRequest): Promise<PaymentDataResponse> {
    return firstValueFrom(this.http.post<PaymentDataResponse>(
      `${this.apiUrl}/v2/dados_pagamento`,
      payload).pipe(take(1)));
  }

  getInstallments(payload: InstallmentsRequest): Promise<InstallmentsResponse> {
    return firstValueFrom(this.http.post<InstallmentsResponse>(
      `${this.apiUrl}/v2/parcelas`,
      payload).pipe(take(1)));
  }
}
